import { useState, useEffect } from 'react';

import { Container } from '@app/components/Container';
import { PageHeader } from '@app/components/PageHeader';
import { Selector } from '@app/components/common/Selector';
import { LoadingSpinner } from '@app/components/common/LoadingSpinner';
import { useError } from '@app/hooks/useError';
import Logger from '@app/utils/logger';
import { VARIABLES, REGIONS } from '@app/components/common/constants';
import {
  BaseLiveBenchmarkingModelWithAvailability,
  ForecastModel,
  LIVE_BENCHMARKING_MODELS,
} from '@app/common/static-data/models';
import { ModelSelector } from '@app/components/common/ModelSelector';
import { useAccess } from '@app/components/access';
import { PerformanceMetric } from '@app/api/live-benchmarking/types';
import { BenchmarkingService } from '@app/api/live-benchmarking';

import { PerformanceChart } from './components/PerformanceChart';
import { TimeSeriesChart } from './components/TimeSeriesChart';

type VariableId = (typeof VARIABLES)[number]['id'];
type RegionId = (typeof REGIONS)[number]['id'];
type MetricType = 'rmse' | 'mbe';

const today = new Date();
const startDate = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());

export default function LiveBenchmarking() {
  const { access, isLoading: isAccessLoading } = useAccess();
  const [selectedRegion, setSelectedRegion] = useState<RegionId>('europe');
  const [selectedVariable, setSelectedVariable] = useState<VariableId>('2t');
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('rmse');
  const [selectedModels, setSelectedModels] = useState<ForecastModel[]>(
    LIVE_BENCHMARKING_MODELS.map((model) => model.id as ForecastModel),
  );
  const [performanceData, setPerformanceData] = useState<PerformanceMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const { showError } = useError();
  const [leadTime, setLeadTime] = useState<number>(24);
  const [availableModels, setAvailableModels] = useState<
    BaseLiveBenchmarkingModelWithAvailability[]
  >([]);

  useEffect(() => {
    if (isAccessLoading || !access || !access.permissions.has('read:forecasts')) {
      setAvailableModels([]);
      return;
    }
    const visible_models = LIVE_BENCHMARKING_MODELS.filter(
      (m) => access.features.has('ept2') || m.id !== 'ept2',
    );
    const available_model_names = access.resources.get('read:forecasts') || new Set();

    setAvailableModels(
      visible_models.map((val) => ({ ...val, available: available_model_names.has(val.id) })),
    );

    const filteredSelectedModels = selectedModels.filter((v) => available_model_names.has(v));
    if (filteredSelectedModels.length !== selectedModels.length) {
      setSelectedModels(filteredSelectedModels);
    }
  }, [access, isAccessLoading, selectedModels]);

  useEffect(() => {
    let isMounted = true;

    const loadPerformanceData = async () => {
      if (!isMounted) return;
      setLoading(true);

      if (isAccessLoading) {
        return;
      }

      try {
        const performanceData = await BenchmarkingService.getLiveBenchmarks(startDate);
        if (isMounted) {
          setPerformanceData(performanceData.metrics);
        }
      } catch (error) {
        Logger.error('Failed to load performance data', error);
        showError({
          code: 'LIVE_BENCHMARKING_ERROR',
          message: 'Failed to load performance data for live benchmarking',
          severity: 'error',
          suggestion: 'Please try again later.',
        });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPerformanceData();

    return () => {
      isMounted = false;
    };
  }, [showError, isAccessLoading]);

  const selectedVarConfig = VARIABLES.find((v) => v.id === selectedVariable);
  const variableColumn = selectedVarConfig?.juaId;

  const latestForecastTime = performanceData.reduce((max, metric) => {
    return metric.start_time.getTime() > max.getTime() ? metric.start_time : max;
  }, new Date(0));

  const latestForecastTimeFormatted =
    latestForecastTime.getTime() > 0
      ? latestForecastTime.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short',
          timeZone: 'UTC',
        })
      : null;

  // Filter data based on selected region, metric, and models
  const filteredData = performanceData.filter(
    (metric) =>
      metric.region === selectedRegion &&
      metric.metric === selectedMetric &&
      metric[variableColumn as keyof PerformanceMetric] !== undefined &&
      selectedModels.includes(metric.model as ForecastModel),
  );

  // Filter out SSRD from variables for the selector
  const availableVariables = VARIABLES.filter((variable) => variable.id !== 'ssrd');

  return (
    <Container>
      <PageHeader
        title="Live Benchmarking"
        description="Live model performance benchmarked against IFS HRES Initial Condition ground truth"
      />

      <div className="flex flex-col space-y-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <Selector
            label="Region"
            options={REGIONS.map((region) => ({
              id: region.id,
              label: region.label,
              description: region.description,
            }))}
            value={selectedRegion}
            onChange={(value) => setSelectedRegion(value as RegionId)}
            disabled={loading}
            data-ph-capture-attribute="dropdown-performance-region"
          />

          <Selector
            label="Parameter"
            options={availableVariables.map((variable) => ({
              id: variable.id,
              label: variable.label,
              description: variable.description,
            }))}
            value={selectedVariable}
            onChange={(value) => setSelectedVariable(value as VariableId)}
            disabled={loading}
            data-ph-capture-attribute="dropdown-performance-parameter"
          />

          <Selector
            label="Metric"
            options={[
              { id: 'rmse', label: 'RMSE', description: 'Root Mean Square Error' },
              { id: 'mbe', label: 'MBE', description: 'Mean Bias Error' },
            ]}
            value={selectedMetric}
            onChange={(value) => setSelectedMetric(value as MetricType)}
            disabled={loading}
            data-ph-capture-attribute="dropdown-performance-metric"
          />

          <ModelSelector
            selectedModels={selectedModels}
            models={availableModels}
            onChange={setSelectedModels}
            disabled={loading}
          />
        </div>
      </div>

      <div className="bg-white dark:bg-theme-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <h2 className="text-lg font-bold mb-4 dark:text-theme-dark-text-primary">
          Performance over the last three months
        </h2>
        {/* lead time slider */}
        <div className="flex items-center justify-center space-x-4 mb-4">
          <div className="w-96">
            <label className="block text-sm font-medium text-gray-700 dark:text-theme-dark-text-secondary">
              Lead Time
            </label>
            <input
              type="range"
              min={6}
              max={120}
              step={6}
              value={leadTime}
              data-ph-capture-attribute="input-performance-lead-time"
              onChange={(e) => setLeadTime(Number(e.target.value))}
              className="mt-1 block w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:bg-[#E34290] [&::-moz-range-thumb]:bg-[#E34290]"
              style={{
                WebkitAppearance: 'none',
                appearance: 'none',
                accentColor: '#E34290',
              }}
            />
            <div className="text-sm text-gray-700 dark:text-theme-dark-text-secondary text-right">
              {leadTime} hours
            </div>
          </div>
        </div>

        <div className="mb-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-[300px] space-y-4">
              <LoadingSpinner size="lg" />
              <p className="text-gray-600 dark:text-theme-dark-text-secondary">
                Loading live benchmarking data...
              </p>
            </div>
          ) : filteredData.length > 0 ? (
            <TimeSeriesChart
              data={filteredData}
              selectedVariable={variableColumn || ''}
              selectedMetric={selectedMetric}
              leadTime={leadTime}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-[300px] text-gray-500 dark:text-theme-dark-text-secondary">
              <p>No live benchmark data available for this lead time.</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-theme-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-bold mb-4 dark:text-theme-dark-text-primary">
          Latest forecast{latestForecastTimeFormatted ? ` from ${latestForecastTimeFormatted}` : ''}
        </h2>
        {loading ? (
          <div className="flex flex-col items-center justify-center h-[500px] space-y-4">
            <LoadingSpinner size="lg" />
            <p className="text-gray-600 dark:text-theme-dark-text-secondary">
              Loading performance data...
            </p>
          </div>
        ) : filteredData.length > 0 ? (
          <PerformanceChart
            data={filteredData}
            selectedVariable={variableColumn || ''}
            selectedMetric={selectedMetric}
            latestForecastTime={latestForecastTime}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-[500px] text-gray-500 dark:text-theme-dark-text-secondary">
            <p>No performance data available for the selected region, parameter, and metric.</p>
          </div>
        )}
      </div>
    </Container>
  );
}
